import React from "react";
import { Link } from "react-router-dom";
import { Callout, DocLink, DocSection, DocTable } from "../components/DocPrimitives";
import { statusOf } from "../featureStatus";

export default function GettingStarted() {
    return (
        <>
            <p className="doc-lede">
                This is how I add an app to Gait: set up its workspace in the Gait console and connect its first
                application.
            </p>

            <Callout kind="availability" title="What works today">
                Signing in, creating a workspace, inviting people, managing applications and their connection
                keys, reporting security checks, and reviewing and acting on findings all work in the console now.
                Where something isn't available yet, the page describing it says so.
            </Callout>

            <p>
                New to Gait? Read <DocLink to="people-and-applications">People and applications</DocLink> first. It's
                the one idea everything else builds on.
            </p>

            <DocSection id="the-four-things-youll-create" title="The four things I create">
                <DocTable caption="The four things I create">
                    <thead>
                        <tr>
                            <th scope="col">Thing</th>
                            <th scope="col">What it is</th>
                            <th scope="col">Example</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th scope="row">Workspace</th>
                            <td>
                                An app's private space in Gait. Everything else belongs to it, and nobody outside it
                                can see any of it.
                            </td>
                            <td><code>Lumen</code></td>
                        </tr>
                        <tr>
                            <th scope="row">Application</th>
                            <td>One piece of an app, in <strong>one</strong> environment.</td>
                            <td><code>Lumen API</code> in <code>production</code></td>
                        </tr>
                        <tr>
                            <th scope="row">Environment</th>
                            <td>
                                Where that software runs: <code>local</code>, <code>test</code>, <code>ci</code>,{" "}
                                <code>staging</code> or <code>production</code>. The same software in two environments
                                is two applications.
                            </td>
                            <td><code>local</code> for my laptop, <code>production</code> for the live service</td>
                        </tr>
                        <tr>
                            <th scope="row">Connection key</th>
                            <td>The secret an application uses to prove to Gait that it is that application.</td>
                            <td>Shown once, when it's issued</td>
                        </tr>
                    </tbody>
                </DocTable>
            </DocSection>

            <DocSection id="step-1-sign-in" title="Step 1: Sign in">
                <p>
                    <Link to="/login">Sign in</Link>, or <Link to="/register">create an account</Link>. A password
                    needs at least 12 characters, not only numbers, not a common password, and not too like your name
                    or email address.
                </p>
                <p>
                    Then turn on <DocLink to="two-step-verification">two-step verification</DocLink> from your{" "}
                    <strong>Account</strong> page, so a stolen password isn't enough to get in.
                </p>
                <p>
                    Your browser never holds a long-lived login token that a script could read. Reloading the page
                    keeps you signed in, and <strong>Sign out</strong> ends the session on Gait's side, not just in
                    your browser.
                </p>
                {statusOf("emailVerification") === "live" ? (
                    <p>
                        When you create an account, Gait emails you a link. Confirm your email address with it before
                        you create a workspace or accept an invite; the link lasts 48 hours, and you can ask for a new
                        one from the console.
                    </p>
                ) : (
                    <Callout kind="availability">
                        Email confirmation is rolling out. Once it's live, Gait emails you a link when you create an
                        account, and you'll need to confirm your email address before you can create a workspace or
                        accept an invite.
                    </Callout>
                )}
            </DocSection>

            <DocSection
                id="step-2-create-your-workspace"
                aliases={["step-2-create-your-company"]}
                title="Step 2: Create the app's workspace"
            >
                <p>The first time you open the console, Gait asks you to create a workspace. I create one per app.</p>
                <ul>
                    <li><strong>Workspace name</strong>: what its members will see, e.g. <code>Lumen</code>.</li>
                    <li>
                        <strong>Workspace URL slug</strong>: a short, lowercase identifier used in links, e.g.{" "}
                        <code>lumen</code>. <strong>Choose carefully: it can't be changed later.</strong>
                    </li>
                </ul>
                <p>You become the workspace's <strong>Owner</strong>.</p>
            </DocSection>

            <DocSection id="step-3-add-an-application" title="Step 3: Add an application">
                <p>Add one application per piece of the app <strong>per environment</strong>.</p>
                <ul>
                    <li><strong>Name</strong>: e.g. <code>Lumen API</code>.</li>
                    <li>
                        <strong>Slug</strong>: e.g. <code>lumen-api</code>. You can reuse the same slug in another
                        environment; that's a separate application.
                    </li>
                    <li>
                        <strong>Environment</strong>: where this copy runs. Gait keeps each environment's security
                        picture separate, so pick carefully.
                    </li>
                </ul>
                <p>
                    Tip: start with the environment you're working in today (often <code>local</code>), and add{" "}
                    <code>staging</code> and <code>production</code> applications when you deploy there.
                </p>
            </DocSection>

            <DocSection id="step-4-get-the-connection-key" title="Step 4: Get the connection key">
                <p>Gait shows the application's <strong>connection key exactly once</strong>.</p>
                <ol>
                    <li>Copy it immediately.</li>
                    <li>
                        Put it where the application reads secrets, such as its <code>.env</code> file or the hosting
                        provider's secret settings. Never put it in source code, tickets or chat.
                    </li>
                    <li>
                        If you lose it, issue a new key and revoke the old one. See{" "}
                        <DocLink to="applications-and-connection-keys#rotating-keys">Rotating keys</DocLink>.
                    </li>
                </ol>
            </DocSection>

            <DocSection id="next-steps" title="Next steps">
                <ul>
                    <li>
                        <DocLink to="connecting-your-software">Connect the app</DocLink>: save the backend key,
                        configure Django or FastAPI, verify the connection, and send a first security check.
                    </li>
                    <li>
                        <DocLink to="security-checks-and-findings">Security checks &amp; findings</DocLink>: what happens
                        after an app reports.
                    </li>
                    <li>
                        <DocLink to="teams-roles-and-invites">Teams, roles &amp; invites</DocLink>: invite other people in.
                    </li>
                </ul>
            </DocSection>
        </>
    );
}
